import { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import EnvironmentCameraRecorder from './EnvironmentCameraRecorder';
import {
  getEnvironmentUploadUrls,
  uploadToR2,
  startEnvironmentVerification,
  pollVerificationStatus
} from '../../../../services/quizSessionService';


const STATUS = {
  IDLE: 'IDLE',
  RECORDING: 'RECORDING',
  UPLOADING: 'UPLOADING',
  VERIFYING: 'VERIFYING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED'
};

export default function EnvironmentVerifyStep({ quizId, onNext, onCancel }) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMsg, setErrorMsg] = useState(null);
  const [verificationId, setVerificationId] = useState(null);

  const handleStart = () => {
    setStatus(STATUS.RECORDING);
    setErrorMsg(null);
  };

  const handleCaptureComplete = async (videoBlob, snapshotBlobs) => {
    setStatus(STATUS.UPLOADING);
    try {
      // Step 1: Get Upload URLs
      const urlsData = await getEnvironmentUploadUrls(quizId, snapshotBlobs.length);

      const { video_upload, snapshot_uploads } = urlsData;

      // Step 2: Upload to R2 directly (Parallel)
      const uploadPromises = [];

      // Video upload
      uploadPromises.push(uploadToR2(video_upload.url, videoBlob, 'video/webm'));

      // Snapshot uploads
      snapshotBlobs.forEach((blob, index) => {
        if (snapshot_uploads[index]) {
          uploadPromises.push(uploadToR2(snapshot_uploads[index].url, blob, 'image/jpeg'));
        }
      });

      await Promise.all(uploadPromises);

      // Step 3: Start verification
      setStatus(STATUS.VERIFYING);
      const snapshotKeys = snapshot_uploads.map(u => u.key);
      const startRes = await startEnvironmentVerification(quizId, video_upload.key, snapshotKeys);

      setVerificationId(startRes.verification_id);

    } catch (error) {
      console.error("Environment Verification Error:", error);
      setErrorMsg(error.response?.data?.error || error.message || "Failed to process environment check.");
      setStatus(STATUS.FAILED);
    }
  };

  // Step 4: Poll Status
  useEffect(() => {
    let interval;
    let timeout;
    if (status === STATUS.VERIFYING && verificationId) {
      const poll = async () => {
        try {
          const res = await pollVerificationStatus(verificationId);
          if (res.status === 'VERIFIED') {
            setStatus(STATUS.SUCCESS);
            clearInterval(interval);
            setTimeout(onNext, 1500); // auto proceed after brief success message
          } else if (res.status === 'FAILED') {
            setStatus(STATUS.FAILED);
            setErrorMsg(res.failure_reason || "Environment verification failed.");
            clearInterval(interval);
          }
          // if PENDING, just keep polling
        } catch (error) {
          console.error("Polling error", error);
          setStatus(STATUS.FAILED);
          setErrorMsg("Lost connection to verification server.");
          clearInterval(interval);
        }
      };

      // Wait 10 seconds before the first check to allow backend processing
      timeout = setTimeout(() => {
        poll();
        interval = setInterval(poll, 3000);
      }, 10000);
    }
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [status, verificationId, onNext]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          Environment Verification
        </h2>
        <p className="text-sm text-gray-500">
          We need to verify your testing environment to ensure academic integrity.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border shadow-sm flex flex-col items-center">

        {status === STATUS.IDLE && (
          <div className="text-center space-y-4 max-w-md">
            <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h3 className="font-semibold text-gray-800">Prepare for Environment Sweep</h3>
            <p className="text-sm text-gray-500 pb-4">
              You will be asked to slowly pan your camera around the room and show your desk area.
              This video will be securely recorded and analyzed. Failure to properly show your environment may invalidate your exam.
            </p>
            <button
              onClick={handleStart}
              className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition"
            >
              I Understand, Start Camera
            </button>
          </div>
        )}

        {status === STATUS.RECORDING && (
          <EnvironmentCameraRecorder
            snapshotCount={3}
            onCaptureComplete={handleCaptureComplete}
          />
        )}

        {status === STATUS.UPLOADING && (
          <div className="text-center py-12 space-y-4">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
            <h3 className="font-semibold text-gray-800">Uploading Video...</h3>
            <p className="text-sm text-gray-500">Securely transferring your environment sweep.</p>
          </div>
        )}

        {status === STATUS.VERIFYING && (
          <div className="text-center py-12 space-y-4">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
            <h3 className="font-semibold text-gray-800">Analyzing Environment...</h3>
            <p className="text-sm text-gray-500">This usually takes a few seconds. Please wait.</p>
          </div>
        )}

        {status === STATUS.SUCCESS && (
          <div className="text-center py-12 space-y-4 animate-fade-in">
            <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h3 className="font-semibold text-gray-800">Environment Verified!</h3>
            <p className="text-sm text-gray-500">Proceeding to the next step...</p>
          </div>
        )}

        {status === STATUS.FAILED && (
          <div className="text-center py-8 space-y-4 max-w-md w-full">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h3 className="font-semibold text-gray-800">Verification Failed</h3>
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded text-left border border-red-100">
              {errorMsg}
            </div>
            <p className="text-sm text-gray-500 pt-2">
              Please remove any prohibited items or ensure the lighting is good, then try again.
            </p>
            <button
              onClick={() => setStatus(STATUS.IDLE)}
              className="w-full mt-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Retry Verification
            </button>
          </div>
        )}

      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <button
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
